import '@/i18n';
import { lazy, Suspense } from 'react';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from '@/components/layout';
import { CartProvider } from '@/contexts/CartContext';
import { ThemeProvider } from '@/components/ThemeProvider';

// Eagerly loaded pages (critical path)
import HomePage from '@/pages/HomePage';
import NotFound from '@/pages/NotFound';

// Lazy-loaded public pages
const DataFilesPage = lazy(() => import('@/pages/DataFilesPage'));
const GepositPage = lazy(() => import('@/pages/GepositPage'));
const AboutPage = lazy(() => import('@/pages/AboutPage'));
const ContactPage = lazy(() => import('@/pages/ContactPage'));
const DataInServicesPage = lazy(() => import('@/pages/DataInServicesPage'));
const PostalCodesInfoPage = lazy(() => import('@/pages/PostalCodesInfoPage'));
const PostalCountyPage = lazy(() => import('@/pages/PostalCountyPage'));
const PostalMunicipalityPage = lazy(() => import('@/pages/PostalMunicipalityPage'));
const PostalLocalityPage = lazy(() => import('@/pages/PostalLocalityPage'));
const TermsPage = lazy(() => import('@/pages/TermsPage'));
const FaqPage = lazy(() => import('@/pages/FaqPage'));
const NewsPage = lazy(() => import('@/pages/NewsPage'));
const NewsArticlePage = lazy(() => import('@/pages/NewsArticlePage'));
const ShopPage = lazy(() => import('@/pages/ShopPage'));
const ProductPage = lazy(() => import('@/pages/ProductPage'));
const CartPage = lazy(() => import('@/pages/CartPage'));
const CheckoutPage = lazy(() => import('@/pages/CheckoutPage'));
const OrderConfirmationPage = lazy(() => import('@/pages/OrderConfirmationPage'));
const CookiePolicyPage = lazy(() => import('@/pages/CookiePolicyPage'));
const PrivacyPolicyPage = lazy(() => import('@/pages/PrivacyPolicyPage'));
const CancellationPage = lazy(() => import('@/pages/CancellationPage'));
const CancellationConfirmationPage = lazy(() => import('@/pages/CancellationConfirmationPage'));

// Lazy-loaded admin pages
const AdminLoginPage = lazy(() => import('@/pages/AdminLoginPage'));
const AdminProductsPage = lazy(() => import('@/pages/AdminProductsPage'));
const AdminProductEditPage = lazy(() => import('@/pages/AdminProductEditPage'));
const AdminCategoriesPage = lazy(() => import('@/pages/AdminCategoriesPage'));
const AdminNewsPage = lazy(() => import('@/pages/AdminNewsPage'));
const AdminNewsEditPage = lazy(() => import('@/pages/AdminNewsEditPage'));
const AdminSearchSettingsPage = lazy(() => import('@/pages/AdminSearchSettingsPage'));
const AdminDocumentsPage = lazy(() => import('@/pages/AdminDocumentsPage'));
const AdminAnnouncementPage = lazy(() => import('@/pages/AdminAnnouncementPage'));
const AdminOrdersPage = lazy(() => import('@/pages/AdminOrdersPage'));
const AdminPostalDataPage = lazy(() => import('@/pages/AdminPostalDataPage'));
const AdminUsersPage = lazy(() => import('@/pages/AdminUsersPage'));
const AdminCancellationsPage = lazy(() => import('@/pages/AdminCancellationsPage'));
const AdminCancellationStatsPage = lazy(() => import('@/pages/AdminCancellationStatsPage'));
const AdminCancellationSettingsPage = lazy(() => import('@/pages/AdminCancellationSettingsPage'));

// Lazy-loaded admin components
const AdminGuard = lazy(() => import('@/components/admin/AdminGuard').then(m => ({ default: m.AdminGuard })));
const AdminLayout = lazy(() => import('@/components/admin/AdminLayout').then(m => ({ default: m.AdminLayout })));

const queryClient = new QueryClient();

// Supported languages
const supportedLanguages = ['sv', 'da', 'fi', 'no', 'en'];

function PageSuspense({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<div className="min-h-[60vh]" />}>
      {children}
    </Suspense>
  );
}

function AppRoutes() {
  return (
    <Routes>
      {/* Redirect root to default language */}
      <Route path="/" element={<Navigate to="/sv" replace />} />
      
      {/* Language-prefixed routes */}
      <Route path="/:lang" element={<Layout><HomePage /></Layout>} />
      <Route path="/:lang/datafiler" element={<Layout><PageSuspense><DataFilesPage /></PageSuspense></Layout>} />
      <Route path="/:lang/shop" element={<Layout><PageSuspense><ShopPage /></PageSuspense></Layout>} />
      <Route path="/:lang/shop/:productId" element={<Layout><PageSuspense><ProductPage /></PageSuspense></Layout>} />
      <Route path="/:lang/cart" element={<Layout><PageSuspense><CartPage /></PageSuspense></Layout>} />
      <Route path="/:lang/checkout" element={<Layout><PageSuspense><CheckoutPage /></PageSuspense></Layout>} />
      <Route path="/:lang/order-confirmation" element={<Layout><PageSuspense><OrderConfirmationPage /></PageSuspense></Layout>} />
      <Route path="/:lang/om-oss" element={<Layout><PageSuspense><AboutPage /></PageSuspense></Layout>} />
      <Route path="/:lang/kontakt" element={<Layout><PageSuspense><ContactPage /></PageSuspense></Layout>} />
      <Route path="/:lang/geposit" element={<Layout><PageSuspense><GepositPage /></PageSuspense></Layout>} />
      <Route path="/:lang/information/data-i-vara-tjanster" element={<Layout><PageSuspense><DataInServicesPage /></PageSuspense></Layout>} />
      <Route path="/:lang/information/postnummer" element={<Layout><PageSuspense><PostalCodesInfoPage /></PageSuspense></Layout>} />
      <Route path="/:lang/information/postnummer/:countyCode" element={<Layout><PageSuspense><PostalCountyPage /></PageSuspense></Layout>} />
      <Route path="/:lang/information/postnummer/:countyCode/:municipalityCode" element={<Layout><PageSuspense><PostalMunicipalityPage /></PageSuspense></Layout>} />
      <Route path="/:lang/information/postnummer/:countyCode/:municipalityCode/:localityCode" element={<Layout><PageSuspense><PostalLocalityPage /></PageSuspense></Layout>} />
      <Route path="/:lang/information/kopvillkor" element={<Layout><PageSuspense><TermsPage /></PageSuspense></Layout>} />
      <Route path="/:lang/information/faq" element={<Layout><PageSuspense><FaqPage /></PageSuspense></Layout>} />
      <Route path="/:lang/information/nyheter" element={<Layout><PageSuspense><NewsPage /></PageSuspense></Layout>} />
      <Route path="/:lang/information/nyheter/:newsId" element={<Layout><PageSuspense><NewsArticlePage /></PageSuspense></Layout>} />
      <Route path="/:lang/integritetspolicy" element={<Layout><PageSuspense><PrivacyPolicyPage /></PageSuspense></Layout>} />
      <Route path="/:lang/cookiepolicy" element={<Layout><PageSuspense><CookiePolicyPage /></PageSuspense></Layout>} />
      <Route path="/:lang/uppsagning" element={<Layout><PageSuspense><CancellationPage /></PageSuspense></Layout>} />
      <Route path="/:lang/uppsagning/bekraftelse" element={<Layout><PageSuspense><CancellationConfirmationPage /></PageSuspense></Layout>} />
      
      {/* Admin routes */}
      <Route path="/:lang/admin" element={<Navigate to="products" replace />} />
      <Route path="/:lang/admin/login" element={<Layout><PageSuspense><AdminLoginPage /></PageSuspense></Layout>} />
      <Route path="/:lang/admin/products" element={<Layout><PageSuspense><AdminGuard><AdminLayout><AdminProductsPage /></AdminLayout></AdminGuard></PageSuspense></Layout>} />
      <Route path="/:lang/admin/products/:productId" element={<Layout><PageSuspense><AdminGuard><AdminLayout><AdminProductEditPage /></AdminLayout></AdminGuard></PageSuspense></Layout>} />
      <Route path="/:lang/admin/categories" element={<Layout><PageSuspense><AdminGuard><AdminLayout><AdminCategoriesPage /></AdminLayout></AdminGuard></PageSuspense></Layout>} />
      <Route path="/:lang/admin/orders" element={<Layout><PageSuspense><AdminGuard><AdminLayout><AdminOrdersPage /></AdminLayout></AdminGuard></PageSuspense></Layout>} />
      <Route path="/:lang/admin/news" element={<Layout><PageSuspense><AdminGuard><AdminLayout><AdminNewsPage /></AdminLayout></AdminGuard></PageSuspense></Layout>} />
      <Route path="/:lang/admin/news/:newsId" element={<Layout><PageSuspense><AdminGuard><AdminLayout><AdminNewsEditPage /></AdminLayout></AdminGuard></PageSuspense></Layout>} />
      <Route path="/:lang/admin/documents" element={<Layout><PageSuspense><AdminGuard><AdminLayout><AdminDocumentsPage /></AdminLayout></AdminGuard></PageSuspense></Layout>} />
      <Route path="/:lang/admin/search-settings" element={<Layout><PageSuspense><AdminGuard><AdminLayout><AdminSearchSettingsPage /></AdminLayout></AdminGuard></PageSuspense></Layout>} />
      <Route path="/:lang/admin/announcement" element={<Layout><PageSuspense><AdminGuard><AdminLayout><AdminAnnouncementPage /></AdminLayout></AdminGuard></PageSuspense></Layout>} />
      <Route path="/:lang/admin/postal-data" element={<Layout><PageSuspense><AdminGuard><AdminLayout><AdminPostalDataPage /></AdminLayout></AdminGuard></PageSuspense></Layout>} />
      <Route path="/:lang/admin/users" element={<Layout><PageSuspense><AdminGuard><AdminLayout><AdminUsersPage /></AdminLayout></AdminGuard></PageSuspense></Layout>} />
      <Route path="/:lang/admin/cancellations" element={<Layout><PageSuspense><AdminGuard><AdminLayout><AdminCancellationsPage /></AdminLayout></AdminGuard></PageSuspense></Layout>} />
      <Route path="/:lang/admin/cancellation-stats" element={<Layout><PageSuspense><AdminGuard><AdminLayout><AdminCancellationStatsPage /></AdminLayout></AdminGuard></PageSuspense></Layout>} />
      <Route path="/:lang/admin/cancellation-settings" element={<Layout><PageSuspense><AdminGuard><AdminLayout><AdminCancellationSettingsPage /></AdminLayout></AdminGuard></PageSuspense></Layout>} />
      
      {/* Catch-all */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

const App = () => (
  <ThemeProvider>
    <QueryClientProvider client={queryClient}>
      <CartProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </TooltipProvider>
      </CartProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
